<script setup>
import { ref, onMounted, onBeforeUnmount } from 'vue'
import { useAuthStore } from '@/stores/auth'
import { useRoute, useRouter } from 'vue-router'
import { useToast } from 'vue-toastification'
import { storeToRefs } from 'pinia'
import emitter, { events } from '@/api/eventBus'
import { useUserSettingsStore } from '@/stores/userSettings'
import { useWebsocketsStore } from '@/stores/websockets'
import styles from '@/components/styleVariables'
import { useCallWindowStore } from '@/stores/callWindow'

const websocketsStore = useWebsocketsStore()
const authStore = useAuthStore()
const toast = useToast()
const router = useRouter()
const route = useRoute()
const allRoutes = router.options.routes
const callWindowStore = useCallWindowStore()
const { windowState } = storeToRefs(callWindowStore)

const breadcrumbs = computed(() => {
  const pages = route.path.split('/').splice(1)
  const breadcrumbs = []

  let currentRoute = allRoutes.find(item => item.name === route.name)
  for (let i = 0; i < pages.length; i += 1) {
    breadcrumbs.push({
      title: currentRoute.meta.breadcrumb,
      to: `/${pages.slice(0, pages.length - i).join('/')}`,
    })

    if (currentRoute.parent) {
      currentRoute = currentRoute.parent
    }
    else {
      break
    }
  }

  return breadcrumbs.reverse()
})

const isMenuShown = ref(null)
const navLinks = [
  {
    icon: 'mdi-view-dashboard',
    text: 'Дашборд',
    route: '/',
  },
  {
    icon: 'mdi-domain',
    text: 'Новая заявка',
    route: 'new-ticket',
  },
  // {
  //   icon: "mdi-domain",
  //   text: "Заявка",
  //   route: "ticket",
  // },
  {
    icon: 'mdi-list-box-outline',
    text: 'Список заявок',
    route: 'tickets-list',
  },
  {
    icon: 'mdi-monitor-dashboard',
    text: 'Статус диспетчера',
    route: 'status-board',
  },
  {
    icon: 'mdi-account-details',
    text: 'Информация о клиенте',
    route: 'client-details',
  },
  {
    icon: 'mdi-cog',
    text: 'Настройки',
    route: 'settings',
  },
  {
    icon: 'mdi-phone',
    text: 'Вызовы',
    route: 'calls',
  },
]

const handleLogout = () => {
  authStore.logout()
  router.push('/login')
}

// User Settings
const userSettingsStore = useUserSettingsStore()
const { userAvatarUrl } = storeToRefs(userSettingsStore)

// Errors Handlers
const errorHandler = (errorMessage) => {
  const { code, desc } = errorMessage

  const text = desc?.txt ?? 'нет описания'
  toast.error(`Код ошибки: ${code}: ${text}`)
}

const invalidTokenHandler = (errorMessage) => {
  toast.error(`Код ошибки: ${errorMessage.code}: ${errorMessage.text}`)
  authStore.logout()
  router.push('/login')
  websocketsStore.disconnectSocket()
}

// Hooks
onMounted(async () => {
  emitter.on(events.API_ERROR, errorHandler)
  emitter.on(events.AXIOS_ERROR, errorHandler)
  emitter.on(events.API_WRONG_TOKEN, invalidTokenHandler)

  await userSettingsStore.fetchUserSettings()
  await websocketsStore.connectSocket()
})

onBeforeUnmount(() => {
  emitter.off(events.API_ERROR, errorHandler)
  emitter.off(events.API_WRONG_TOKEN, invalidTokenHandler)
  emitter.off(events.AXIOS_ERROR, errorHandler)

  websocketsStore.disconnectSocket()
})
</script>

<template>
  <v-app>
    <v-navigation-drawer v-model="isMenuShown" :width="325">
      <div class="d-flex flex-column" style="height: 100%">
        <div class="drawer-header d-flex align-center">
          <img
            src="@/assets/images/menu-logo.svg"
            width="24"
            height="24"
            alt="логотип"
          />

          <div class="d-flex justify-space-between align-center flex-grow-1">
            <span class="page-title">Диспетчер</span>
            <v-btn
              variant="text"
              color="white"
              icon="mdi-logout"
              width="24"
              height="24"
              @click="handleLogout"
            ></v-btn>
          </div>
        </div>

        <div class="d-flex ga-4 pl-4 pt-4">
          <user-avatar :userAvatarUrl="userAvatarUrl"></user-avatar>
          <div class="flex-grow-1 d-flex justify-space-between align-center">
            <span v-if="userSettingsStore.userDetails" class="page-table-text">
              {{ userSettingsStore.userDetails.short_name }}
            </span>
          </div>
        </div>

        <v-divider
          :color="styles.colors.brandPrimary"
          thickness="2"
          opacity="100"
          class="mt-4"
        ></v-divider>

        <v-list class="pt-0">
          <div v-for="(link, i) in navLinks" :key="i">
            <v-list-item :to="link.route">
              <template #prepend>
                <v-icon :icon="link.icon" />
              </template>
              <v-list-item-title>
                {{ link.text }}
              </v-list-item-title>
            </v-list-item>
          </div>
        </v-list>
      </div>
    </v-navigation-drawer>

    <v-app-bar app class="pr-4">
      <v-app-bar-nav-icon @click="isMenuShown = !isMenuShown" />

      <v-breadcrumbs class="breadcrumbs" :items="breadcrumbs" />
      <v-spacer></v-spacer>
      <div class="mr-7">
        <call-window-bar v-if="windowState === 'open' || windowState === 'minimized'"></call-window-bar>
      </div>
      <user-state></user-state>
    </v-app-bar>

    <v-main>
      <router-view />
    </v-main>

    <call-window></call-window>

    <v-footer app class="mb-2">
      <v-row>
        <v-col>
          <div class="footer">
            <p class="text-uppercase">События</p>
          </div>
        </v-col>
      </v-row>
    </v-footer>
  </v-app>
</template>

<style lang="scss" scoped>

.breadcrumbs:deep(.v-breadcrumbs-item--disabled) {
  font-weight: regular;
  opacity: initial;
}

.v-breadcrumbs {
  padding: 0px 0px;
}

nav,
.v-app-bar.v-toolbar {
  background-color: rgb(var(--v-theme-brandLight)) !important;
  border: none;
}

.page-title {
  font-size: 24px; //
  line-height: 32px;
  color: white;
  padding-left: 15px;
}

.page-table-text {
  font-size: 14px;
  line-height: 16px;
}

.drawer-header {
  background-color: rgb(var(--v-theme-brandPrimary));
  padding: 16px 16px;
}

.footer {
  height: 80px;
  border-radius: 3pxs;
  padding: 10px 10px;
  border: 1px solid rgb(var(--v-theme-brandGray));
  font-size: 12px;
  line-height: 14px;
  color: rgb(var(--v-theme-brandDarkGray));
}
</style>
